import React from 'react';
import styled from 'styled-components';

import { formatNumber } from '@column/column-ui-kit';

import { CopyRow } from '~/components/CopyRow';
import { InterestConfig } from '~/repositories/InterestRepository';
import { Card, Flex, Inner, Text } from '~/styles';

interface InterestCalculationBreakdownProps {
  payoutAmount: number;
  currencyCode: string;
  lastAccruedDate: string;
  interestConfig?: InterestConfig;
  balance?: number;
}

const StyledCard = styled(Card)`
  min-width: 248px;
  margin-bottom: 16px;
  margin-left: 28px;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    margin-left: 0;
  }
`;

const CopyRowList = styled.ul`
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
  gap: 0;

  li {
    border-top: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};

    &:first-child {
      border-top: none;
    }
  }
`;

const formatRate = (rate: string | undefined): string => {
  if (!rate) return 'N/A';
  const rateNum = parseFloat(rate);
  if (isNaN(rateNum)) return rate;
  return `${(rateNum * 100).toFixed(4)}%`;
};

export const InterestCalculationBreakdown: React.FC<InterestCalculationBreakdownProps> = ({
  payoutAmount,
  currencyCode,
  interestConfig,
  balance,
}) => {
  if (!interestConfig) {
    return (
      <StyledCard>
        <CopyRowList>
          <li>
            <CopyRow label="Interest Calculation" value="Interest configuration not available" />
          </li>
        </CopyRowList>
      </StyledCard>
    );
  }

  const effectiveRate = interestConfig.tiers?.[0]?.fixedRate || interestConfig.tiers?.[0]?.pivotPercentage || '0';
  const annualRate = parseFloat(effectiveRate);
  const daysInYear = interestConfig.accrualMethod === 'actual_365' ? 365 : 360;
  const dailyRateDecimal = annualRate / daysInYear;

  const payoutAmountInCents = typeof payoutAmount === 'string' ? parseFloat(payoutAmount) : payoutAmount;
  const balanceInCents = balance || 0;

  let calculatedDays = 0;
  let calculatedDailyInterest = 0;
  if (balanceInCents > 0 && dailyRateDecimal > 0) {
    const balanceInMicros = balanceInCents * 10000;
    const dailyInterestInMicros = Math.floor(balanceInMicros * dailyRateDecimal);
    calculatedDailyInterest = Math.floor(dailyInterestInMicros / 10000);
    if (calculatedDailyInterest > 0) {
      calculatedDays = Math.round(payoutAmountInCents / calculatedDailyInterest);
    }
  }

  return (
    <StyledCard>
      <CopyRowList>
        {balanceInCents > 0 && (
          <li>
            <CopyRow label="Average Balance" value={formatNumber(balanceInCents, currencyCode)} />
          </li>
        )}

        {interestConfig.tiers && interestConfig.tiers.length > 0 && (
          <li>
            <CopyRow label="Annual Interest Rate" value={formatRate(effectiveRate)} variant="secondary" />
          </li>
        )}

        <li>
          <CopyRow label="Daily Interest Rate" value={formatRate(dailyRateDecimal.toString())} variant="secondary" />
        </li>

        {interestConfig.pivotRate && (
          <li>
            <CopyRow label="Pivot Rate" value={formatRate(interestConfig.pivotRate.rate)} variant="secondary" />
          </li>
        )}

        {calculatedDays > 0 && (
          <li>
            <CopyRow
              label="Accrual Period"
              value={`${calculatedDays} day${calculatedDays !== 1 ? 's' : ''}`}
              variant="secondary"
            />
          </li>
        )}

        <li>
          <CopyRow
            label="Interest Payout"
            value={
              <Inner pl={36} py={16} pr={16}>
                <Flex gap={4} alignItems="center">
                  <Text color="foreground" weight="medium">
                    {formatNumber(payoutAmountInCents, currencyCode)}
                  </Text>
                  <Text color="light" weight="medium">
                    {currencyCode || 'USD'}
                  </Text>
                </Flex>
              </Inner>
            }
          />
        </li>

        <li>
          <Inner py={12} px={16}>
            <Text as="p" size={12} color="lighter">
              {calculatedDays > 0 && calculatedDailyInterest > 0
                ? `Daily Interest (${formatNumber(calculatedDailyInterest, currencyCode)}) × ${calculatedDays} day${calculatedDays !== 1 ? 's' : ''} ≈ Interest Payout`
                : 'Interest is calculated daily and summed over the accrual period'}
            </Text>
          </Inner>
        </li>
      </CopyRowList>
    </StyledCard>
  );
};
